import { useCallback, useEffect, useState } from 'react'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'

/**
 * Follow / Following, wherever a writer's name shows up — the feed, the song
 * room, and the profile itself. Renders nothing on your own card; you cannot
 * follow yourself, and an absent button says that better than a disabled one.
 *
 * Optimistic: the label flips before the network answers, and un-flips on
 * error, because a tap that visibly does nothing for half a second reads as
 * broken on a phone.
 */
export default function FollowButton({ writerId, prominent }) {
  const { user } = useSession()
  const [following, setFollowing] = useState(false)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!user || !writerId || writerId === user.id) return
    const { data } = await supabase.from('follows').select('follower_id')
      .eq('follower_id', user.id).eq('writer_id', writerId).maybeSingle()
    setFollowing(!!data)
    setReady(true)
  }, [user, writerId])

  useEffect(() => { load() }, [load])

  if (!user || !writerId || writerId === user.id) return null

  const toggle = async () => {
    const was = following
    setFollowing(!was)
    setBusy(true)
    const { error } = was
      ? await supabase.from('follows').delete()
          .eq('follower_id', user.id).eq('writer_id', writerId)
      : await supabase.from('follows').insert({ follower_id: user.id, writer_id: writerId })
    setBusy(false)
    if (error) {
      setFollowing(was) // revert — the tap didn't land
      // Nowhere to put a message on a button this small; the reverted state
      // is the whole signal.
      console.error(readableError(error))
    }
  }

  // In the feed this is one control among several and stays compact. On a
  // profile it is the screen's single primary action, which is what `prominent`
  // buys — and only until it's been taken: "Following" is a state, and a state
  // that keeps shouting reads as an action you still have to do.
  const cls = prominent
    ? `btn${following ? '' : ' btn-primary'}`
    : `iconbtn${following ? ' on' : ''}`

  return (
    <button className={cls} onClick={toggle}
            disabled={busy || !ready} aria-pressed={following}>
      {following ? 'Following' : 'Follow'}
    </button>
  )
}
